import { DimensionSlice, PricePoint, TimePoint } from "../../../types";

export interface MonthlyPoint extends TimePoint {
  /** Short, locale-aware axis label, e.g. "Jan 25". */
  label: string;
}

/**
 * Formats a 1-based `month` of `year` as a short axis label. Built from a UTC date so the label
 * never slips a month in negative-offset timezones.
 */
export const monthLabel = (
  year: number,
  month: number,
  locale: string = "en"
): string =>
  new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(locale, {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });

/**
 * Keeps only the points of the active currency (never mix currencies on one axis), sorted
 * chronologically, each with its axis label. A single point is returned as-is: nothing here
 * depends on the series length.
 */
export const toMonthlySeries = (
  timeSeries: TimePoint[] | undefined,
  currency: string,
  locale: string = "en"
): MonthlyPoint[] =>
  (timeSeries ?? [])
    .filter((p) => p.currency === currency)
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((p) => ({ ...p, label: monthLabel(p.year, p.month, locale) }));

export interface DimensionBar {
  id: string;
  label: string;
  currency: string;
  spend: number;
  awards: number;
}

/**
 * Sorts one dimension's slices by spend (descending), keeps the top `topN` and folds the rest into
 * a single "Other" bar (spend and awards summed). The caller passes slices of ONE currency, so the
 * fold never mixes currencies. No "Other" bar is produced when everything fits in `topN`.
 */
export const toTopNWithOther = (
  slices: DimensionSlice[] | undefined,
  topN: number,
  otherLabel: string
): DimensionBar[] => {
  const sorted = [...(slices ?? [])].sort((a, b) => b.spend - a.spend);
  if (sorted.length <= topN) {
    return sorted;
  }
  const head = sorted.slice(0, topN);
  const tail = sorted.slice(topN);
  return [
    ...head,
    {
      id: "__other__",
      label: otherLabel,
      currency: tail[0].currency,
      spend: tail.reduce((sum, s) => sum + s.spend, 0),
      awards: tail.reduce((sum, s) => sum + s.awards, 0),
    },
  ];
};

export interface IndexedFamily {
  id: string;
  family: string;
  /** % change of the family's last indexed value vs. its base of 100 (0 for a single point). */
  changePct: number;
  /** The family's last indexed value (100 for a single point). */
  latestIndex: number;
}

export interface PriceIndexData {
  /** One row per month (chronological): `label` plus `[familyId]: index` where the family has a point. */
  rows: Array<Record<string, number | string>>;
  /** Every family of the active currency, largest absolute change first (movers order). */
  families: IndexedFamily[];
}

/**
 * Turns the raw price series into an index-to-100 view for ONE currency (never mixes currencies).
 * Each family is indexed to 100 at ITS OWN first month in the window: `avgUnitPrice / first * 100`.
 * A family with a single point is just 100 there (change 0) — there is no (n-1) division anywhere.
 * Points with a non-positive/non-finite price are dropped (they would index to NaN/Infinity), and a
 * family left with no valid points is omitted.
 */
export const toPriceIndex = (
  priceSeries: PricePoint[] | undefined,
  currency: string,
  locale: string = "en"
): PriceIndexData => {
  const valid = (priceSeries ?? [])
    .filter(
      (p) =>
        p.currency === currency &&
        Number.isFinite(p.avgUnitPrice) &&
        p.avgUnitPrice > 0
    )
    .sort((a, b) => a.year - b.year || a.month - b.month);

  const byFamily = new Map<string, PricePoint[]>();
  valid.forEach((p) => {
    const list = byFamily.get(p.familyId);
    if (list) {
      list.push(p);
    } else {
      byFamily.set(p.familyId, [p]);
    }
  });

  const rowsByMonth = new Map<string, Record<string, number | string>>();
  const families: IndexedFamily[] = [];
  byFamily.forEach((points, id) => {
    const base = points[0].avgUnitPrice;
    let latestIndex = 100;
    points.forEach((p) => {
      const key = `${p.year}-${String(p.month).padStart(2, "0")}`;
      const row = rowsByMonth.get(key) ?? {
        key,
        label: monthLabel(p.year, p.month, locale),
      };
      latestIndex = (p.avgUnitPrice / base) * 100;
      row[id] = latestIndex;
      rowsByMonth.set(key, row);
    });
    families.push({
      id,
      family: points[0].family,
      changePct: latestIndex - 100,
      latestIndex,
    });
  });

  families.sort(
    (a, b) =>
      Math.abs(b.changePct) - Math.abs(a.changePct) ||
      a.family.localeCompare(b.family)
  );
  const rows = [...rowsByMonth.values()].sort((a, b) =>
    String(a.key).localeCompare(String(b.key))
  );
  return { rows, families };
};
